import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Plus,
  Trash2,
  Edit2,
  RefreshCw,
  Globe,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  TestTube,
  Eye
} from 'lucide-react';
import AdminSecurityHelpButton from './AdminSecurityHelpButton';
import api from '../../api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

interface IpConfig {
  id: number;
  tenant_id: number | null;
  scope: 'system_ops' | 'admin_routes' | 'all_routes';
  cidr_range: string;
  description: string | null;
  enabled: boolean;
  created_by: number;
  created_at?: string;
  createdAt?: string;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
}

interface WhitelistStatus {
  scopes: {
    [key: string]: {
      enabled: boolean;
      total_rules: number;
      enabled_rules: number;
      disabled_rules: number;
    };
  };
  current_ip: string;
}

const IpWhitelistManagement: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const [configs, setConfigs] = useState<IpConfig[]>([]);
  const [status, setStatus] = useState<WhitelistStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedScope, setSelectedScope] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<IpConfig | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  const [formData, setFormData] = useState({
    scope: 'admin_routes' as 'system_ops' | 'admin_routes' | 'all_routes',
    cidr_range: '',
    description: '',
    enabled: true
  });

  const [testData, setTestData] = useState({
    ip: '',
    scope: 'admin_routes' as string
  });

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedScope !== 'all') {
        params.scope = selectedScope;
      }

      const [configsRes, statusRes] = await Promise.all([
        api.get('/api/admin/ip-whitelist', { params }),
        api.get('/api/admin/ip-whitelist/status')
      ]);

      setConfigs(configsRes.data.ip_configs || []);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching IP configs:', error);
      toast.error('Failed to load IP whitelist configurations');
    } finally {
      setLoading(false);
    }
  }, [selectedScope]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.cidr_range) {
      toast.error('IP address or CIDR range is required');
      return;
    }

    try {
      if (editingConfig) {
        await api.put(`/api/admin/ip-whitelist/${editingConfig.id}`, formData);
        toast.success('IP whitelist entry updated');
      } else {
        await api.post('/api/admin/ip-whitelist', formData);
        toast.success('IP whitelist entry created');
      }

      setShowAddModal(false);
      setEditingConfig(null);
      setFormData({ scope: 'admin_routes', cidr_range: '', description: '', enabled: true });
      fetchConfigs();
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Operation failed';
      toast.error(message);
    }
  };

  const handleDelete = async (config: IpConfig) => {
    if (!confirm(`Delete IP whitelist entry for ${config.cidr_range}?`)) return;

    try {
      await api.delete(`/api/admin/ip-whitelist/${config.id}`);
      toast.success('IP whitelist entry deleted');
      fetchConfigs();
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Delete failed';
      toast.error(message);
    }
  };

  const handleToggle = async (config: IpConfig) => {
    try {
      await api.put(`/api/admin/ip-whitelist/${config.id}`, { enabled: !config.enabled });
      toast.success(`Rule ${config.enabled ? 'disabled' : 'enabled'}`);
      fetchConfigs();
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Toggle failed';
      toast.error(message);
    }
  };

  const handleTest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!testData.ip) {
      toast.error('IP address is required');
      return;
    }

    try {
      const response = await api.post('/api/admin/ip-whitelist/test', testData);
      setTestResult(response.data);
    } catch (error: any) {
      toast.error('Test failed');
    }
  };

  const openEditModal = (config: IpConfig) => {
    setEditingConfig(config);
    setFormData({
      scope: config.scope,
      cidr_range: config.cidr_range,
      description: config.description || '',
      enabled: config.enabled
    });
    setShowAddModal(true);
  };

  const getScopeLabel = (scope: string) => {
    switch (scope) {
      case 'system_ops': return 'System Operations (Super Admin)';
      case 'admin_routes': return 'Admin Routes';
      case 'all_routes': return 'All Routes';
      default: return scope;
    }
  };

  const getScopeBadgeColor = (scope: string) => {
    switch (scope) {
      case 'system_ops': return 'bg-red-100 text-red-800';
      case 'admin_routes': return 'bg-orange-100 text-orange-800';
      case 'all_routes': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              IP Whitelist Management
            </h2>
            <p className="text-gray-600 mt-1">
              Configure IP address restrictions for different access scopes
            </p>
          </div>
          <div className="flex gap-2">
            <AdminSecurityHelpButton screenId="ip-whitelist" />
            <button
              onClick={() => setShowTestModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <TestTube className="w-4 h-4" />
              Test IP
            </button>
            <button
              onClick={fetchConfigs}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => {
                setEditingConfig(null);
                setFormData({ scope: 'admin_routes', cidr_range: '', description: '', enabled: true });
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add IP Rule
            </button>
          </div>
        </div>

        {status && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-5 h-5 text-gray-600" />
              <span className="font-medium">Your Current IP:</span>
              <code className="bg-white px-2 py-1 rounded border text-sm">{status.current_ip}</code>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(status.scopes).map(([scope, data]) => (
                <div key={scope} className="bg-white rounded-lg p-3 border">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getScopeBadgeColor(scope)}`}>
                      {getScopeLabel(scope)}
                    </span>
                    {data.enabled ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle className="w-4 h-4" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400 text-sm">
                        <XCircle className="w-4 h-4" /> Disabled
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    {data.enabled_rules} enabled / {data.total_rules} total rules
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Important Notes:</p>
              <ul className="mt-1 text-amber-700 list-disc list-inside space-y-1">
                <li>IP whitelisting is only active when at least one enabled rule exists for a scope</li>
                <li>Super Admins always bypass IP restrictions (cannot be locked out)</li>
                <li>Self-lockout prevention: Cannot delete/disable the last rule if it would block your IP</li>
                <li>Use CIDR notation for ranges (e.g., 192.168.1.0/24) or single IPs (e.g., 192.168.1.1)</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSelectedScope('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedScope === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Scopes
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setSelectedScope('system_ops')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedScope === 'system_ops' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              System Ops
            </button>
          )}
          <button
            onClick={() => setSelectedScope('admin_routes')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedScope === 'admin_routes' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Admin Routes
          </button>
          <button
            onClick={() => setSelectedScope('all_routes')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedScope === 'all_routes' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Routes
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : configs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No IP whitelist rules configured</p>
            <p className="text-sm mt-1">Add rules to enable IP-based access control</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Scope</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">CIDR Range</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Description</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Created By</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Created</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((config) => (
                  <tr key={config.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleToggle(config)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                          config.enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {config.enabled ? (
                          <>
                            <CheckCircle className="w-4 h-4" /> Enabled
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4" /> Disabled
                          </>
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getScopeBadgeColor(config.scope)}`}>
                        {getScopeLabel(config.scope)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">{config.cidr_range}</code>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{config.description || '-'}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {config.creator?.name || config.creator?.email || '-'}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {(() => {
                        const raw = config.created_at || config.createdAt;
                        if (!raw) return '-';
                        const d = new Date(raw);
                        return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                      })()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditModal(config)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(config)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold mb-4">
              {editingConfig ? 'Edit IP Whitelist Rule' : 'Add IP Whitelist Rule'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                <select
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!!editingConfig}
                >
                  {isSuperAdmin && <option value="system_ops">System Operations (Super Admin)</option>}
                  <option value="admin_routes">Admin Routes</option>
                  <option value="all_routes">All Routes (Optional)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.scope === 'system_ops' && 'Restricts access to Super Admin system routes'}
                  {formData.scope === 'admin_routes' && 'Restricts access to admin dashboard and settings'}
                  {formData.scope === 'all_routes' && 'Restricts access to all authenticated API routes'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IP Address / CIDR Range
                </label>
                <input
                  type="text"
                  value={formData.cidr_range}
                  onChange={(e) => setFormData({ ...formData, cidr_range: e.target.value })}
                  placeholder="e.g., 192.168.1.0/24 or 10.0.0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Single IP or CIDR notation. Single IPs are treated as /32.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Office network, VPN range"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="enabled" className="text-sm text-gray-700">Enabled</label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingConfig(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingConfig ? 'Update' : 'Add Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <TestTube className="w-5 h-5" />
              Test IP Address
            </h3>
            <form onSubmit={handleTest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                <input
                  type="text"
                  value={testData.ip}
                  onChange={(e) => setTestData({ ...testData, ip: e.target.value })}
                  placeholder="e.g., 192.168.1.100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                <select
                  value={testData.scope}
                  onChange={(e) => setTestData({ ...testData, scope: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {isSuperAdmin && <option value="system_ops">System Operations</option>}
                  <option value="admin_routes">Admin Routes</option>
                  <option value="all_routes">All Routes</option>
                </select>
              </div>

              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Test
              </button>

              {testResult && (
                <div className={`p-4 rounded-lg ${
                  testResult.result === 'allowed' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.result === 'allowed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-medium ${
                      testResult.result === 'allowed' ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {testResult.result === 'allowed' ? 'IP Allowed' : 'IP Blocked'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{testResult.reason}</p>
                  {testResult.matching_rules?.length > 0 && (
                    <div className="mt-2 text-sm">
                      <span className="font-medium">Matching rules:</span>
                      <ul className="list-disc list-inside">
                        {testResult.matching_rules.map((rule: any) => (
                          <li key={rule.id}>{rule.cidr_range} - {rule.description || 'No description'}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </form>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestResult(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IpWhitelistManagement;
